// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useState } from 'preact/hooks';
import type { WalletTx } from '../api.js';
import { formatBtc } from '../format.js';
import { AddressReveal } from './address-reveal.js';

/** Format a unix-seconds timestamp as "YYYY-MM-DD HH:MM:SS UTC". */
export function formatTxTime(unix: number): string {
  return new Date(unix * 1000).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

/**
 * The shared on-chain transaction list — one expandable row per tx, amounts in
 * BTC, newest-first as esplora returns them. Reused by the personal wallet
 * (`wallet.tsx`) and the shared-vault detail page (COB-225) so the two
 * histories render identically and can never drift. Styling lives in
 * `styles/wallet.css` (`wallet-tx-*`); the consuming page imports that sheet.
 * The counterparty renders through `AddressReveal`, which imports its own
 * component-scoped sheet (COB-359).
 */
export function TransactionList({ txs }: { txs: WalletTx[] }) {
  return (
    <div class="wallet-tx-list">
      {txs.map((tx) => (
        <WalletTxRow key={tx.txid} tx={tx} />
      ))}
    </div>
  );
}

function WalletTxRow({ tx }: { tx: WalletTx }) {
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded(!expanded);
  return (
    <div
      class={`wallet-tx-row${expanded ? ' wallet-tx-row--expanded' : ''}`}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
    >
      <div class="wallet-tx-summary">
        <div class="wallet-tx-left">
          {tx.status.block_time ? (
            <span class="wallet-tx-time tabular-nums">
              {new Date(tx.status.block_time * 1000).toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          ) : (
            <span class="wallet-tx-time wallet-tx-time--pending">Unconfirmed</span>
          )}
          {tx.counterparty && (
            /* COB-359: the API now sends the FULL counterparty address (the
               withdrawal destination on debits, the sender on credits — same
               spot, same treatment); AddressReveal owns the truncated rest
               state and the click-to-expand + Copy recovery. Clicks/keys on
               this nested control must not also toggle the row detail. */
            <span
              class="wallet-tx-counterparty"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <AddressReveal address={tx.counterparty} />
            </span>
          )}
        </div>
        <span class={`wallet-tx-amount tabular-nums${tx.direction === 'credit' ? ' wallet-tx-amount--credit' : ' wallet-tx-amount--debit'}`}>
          {tx.direction === 'credit' ? '+' : '−'}{formatBtc(tx.amount_sats)} BTC
        </span>
      </div>
      {expanded && (
        <div class="wallet-tx-detail">
          <div class="wallet-tx-detail-row">
            <span class="wallet-tx-detail-label">Txid</span>
            <span class="wallet-tx-detail-value mono">{tx.txid}</span>
          </div>
          <div class="wallet-tx-detail-row">
            <span class="wallet-tx-detail-label">Status</span>
            <span class="wallet-tx-detail-value">{tx.status.confirmed ? 'Confirmed' : 'Unconfirmed'}</span>
          </div>
          {tx.status.block_time && (
            <div class="wallet-tx-detail-row">
              <span class="wallet-tx-detail-label">Time</span>
              <span class="wallet-tx-detail-value">{formatTxTime(tx.status.block_time)}</span>
            </div>
          )}
          <div class="wallet-tx-detail-row">
            <span class="wallet-tx-detail-label">Fee</span>
            <span class="wallet-tx-detail-value tabular-nums">{formatBtc(tx.fee)} BTC</span>
          </div>
        </div>
      )}
    </div>
  );
}
